/**
 * Content block: video.
 * Poster frame with overlaid copy; clicking anywhere on it swaps the player
 * into the same stage and starts playback — Plyr for uploaded videos, the
 * YouTube iframe for entries with a YouTube URL. An optional teaser loop
 * autoplays muted over the poster while the block is near the viewport.
 * Falls back to the native player when Plyr fails to load.
 */
if (!customElements.get('content-block-video')) {
  customElements.define(
    'content-block-video',
    class ContentBlockVideo extends HTMLElement {
      connectedCallback() {
        // The whole poster is the click target; the Watch Video button inside
        // it stays the focusable element for keyboard users (its activation
        // bubbles up to this same listener).
        this.poster = this.querySelector('[data-video-poster]');
        if (this.poster) this.poster.addEventListener('click', () => this.activate());
        this.initTeaser();
      }

      disconnectedCallback() {
        if (this.teaserObserver) this.teaserObserver.disconnect();
      }

      initTeaser() {
        const teaser = this.querySelector('[data-teaser]');
        if (
          !teaser ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
          (navigator.connection && navigator.connection.saveData)
        ) {
          return;
        }

        // Small rendition on phones — it's an ambient loop, not the content.
        // The sized renditions are URL-derived (Liquid can't enumerate them),
        // so fall back to the guaranteed top rendition if one is missing.
        teaser.src = window.matchMedia('(min-width: 750px)').matches
          ? teaser.dataset.src
          : teaser.dataset.srcSm || teaser.dataset.src;
        teaser.addEventListener(
          'error',
          () => {
            if (teaser.dataset.srcFull && teaser.src !== teaser.dataset.srcFull) {
              teaser.src = teaser.dataset.srcFull;
              teaser
                .play()
                .then(() => teaser.classList.add('is-playing'))
                .catch(() => {});
            }
          },
          { once: true }
        );

        // Fetch and play only near the viewport; pause offscreen. When
        // autoplay is blocked (e.g. iOS Low Power Mode) play() rejects and
        // the teaser stays transparent — the poster underneath is the
        // designed fallback.
        this.teaserObserver = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              teaser.muted = true;
              teaser
                .play()
                .then(() => teaser.classList.add('is-playing'))
                .catch(() => {});
            } else {
              teaser.pause();
            }
          },
          { rootMargin: '200px' }
        );
        this.teaserObserver.observe(this);
      }

      activate() {
        if (this.activated) return;
        this.activated = true;
        if (this.teaserObserver) this.teaserObserver.disconnect();
        if (window.pauseAllMedia) window.pauseAllMedia();

        const template = this.querySelector('template');
        this.appendChild(template.content.cloneNode(true));
        if (this.poster) this.poster.remove();

        // YouTube entries mount an iframe (autoplay via URL param) — no Plyr.
        const frame = this.querySelector('iframe');
        if (frame) {
          frame.focus();
          return;
        }

        const video = this.querySelector('video');
        if (!video) return;

        if (window.Plyr) {
          this.player = new Plyr(video, {
            controls: [
              'play-large',
              'play',
              'progress',
              'current-time',
              'duration',
              'mute',
              'volume',
              'settings',
              'fullscreen',
            ],
            settings: ['speed'],
            speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
            ratio: '16:9',
            iconUrl: this.dataset.iconUrl,
            tooltips: { controls: true, seek: true },
          });
          this.player.play();
          const container = this.querySelector('.plyr');
          if (container) container.focus();
        } else {
          video.setAttribute('controls', '');
          video.play();
          video.focus();
        }
      }
    }
  );
}
